import { BaseApiClient } from "@/lib/api/baseApiClient.service";
import type {
	LoginRequest,
	LoginResponse,
	RegisterRequest,
	RegisterResponse,
	User,
} from "@/lib/auth/auth.types";
import {
	type Auth as FirebaseAuth,
	GoogleAuthProvider,
	OAuthProvider,
	createUserWithEmailAndPassword,
	getAuth,
	getRedirectResult,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signInAnonymously,
	signInWithPopup,
	signInWithRedirect,
	signOut,
	updateProfile,
	verifyBeforeUpdateEmail,
	linkWithCredential,
	linkWithPopup,
	EmailAuthProvider,
} from "firebase/auth";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { captureAuthException } from "@/lib/auth/captureAuthException";
import {
	blockOAuthInInAppBrowser,
	isInAppBrowserAuthError,
} from "@/lib/auth/inAppBrowser";

type AuthResponse = {
	token: string;
};

type BackendUpgradeAnonymousAccountResponse = {
	success: boolean;
	userId: string;
	email: string;
	isAnonymous: boolean;
};

/**
 * Authentication client
 * - uses firebase for authentication
 * - provides information about the user
 */
class AuthService {
	private static instance: AuthService;
	public auth: FirebaseAuth;
	// we are using the base api client to authenticate with the backend
	private baseApi: BaseApiClient;

	private constructor() {
		this.auth = getAuth();
		this.baseApi = BaseApiClient.getInstance();
	}

	/**
	 * True when a popup could not be opened and redirect is the right fallback.
	 * Only `auth/popup-blocked` — not cancelled/concurrent popup requests
	 * (e.g. double-click), which would otherwise trigger a surprise full-page
	 * redirect.
	 */
	private shouldFallbackToRedirect(error: unknown): boolean {
		if (!error || typeof error !== "object" || !("code" in error)) {
			return false;
		}
		return String((error as { code?: string }).code) === "auth/popup-blocked";
	}

	/**
	 * Start OAuth via popup, falling back to redirect only when the popup is
	 * blocked. Prefer popup: Safari 16.1+ / modern browsers break
	 * signInWithRedirect when authDomain is on *.firebaseapp.com while the app
	 * runs on a custom domain (third-party storage partitioning).
	 * @see https://firebase.google.com/docs/auth/web/redirect-best-practices
	 */
	private async signInWithProvider(
		provider: GoogleAuthProvider | OAuthProvider,
		providerKey: "google" | "apple",
	) {
		blockOAuthInInAppBrowser();
		try {
			return await signInWithPopup(this.auth, provider);
		} catch (error: unknown) {
			if (!this.shouldFallbackToRedirect(error)) {
				captureAuthException(error, "provider_popup", {
					provider: providerKey,
				});
				throw error;
			}
			captureAuthException(error, "provider_popup_fallback_redirect", {
				provider: providerKey,
			});
			sessionStorage.setItem("pending_auth_redirect", providerKey);
			await signInWithRedirect(this.auth, provider);
			return null; // page is navigating away
		}
	}

	private async postUpgradeAnonymousAccountToBackend(
		firebaseToken: string,
		email: string,
	): Promise<BackendUpgradeAnonymousAccountResponse> {
		try {
			return await this.baseApi.postWithBearerToken<
				BackendUpgradeAnonymousAccountResponse,
				{ email: string }
			>("anonymous/upgrade-anonymous-account", { email }, firebaseToken);
		} catch (error: unknown) {
			console.error("Failed to sync upgraded account with backend", error);
			captureAuthException(error, "upgrade_anonymous_account_backend");
			throw new Error(error instanceof Error ? error.message : "Unknown error");
		}
	}

	public static getInstance(): AuthService {
		if (!AuthService.instance) {
			AuthService.instance = new AuthService();
		}
		return AuthService.instance;
	}

	/**
	 * Get current user profile and limits
	 */
	public async getMe(): Promise<Partial<User>> {
		try {
			const response = await this.baseApi.get<Partial<User>>("session/me");
			return response;
		} catch (error) {
			console.error("Failed to fetch user profile", error);
			captureAuthException(error, "get_me");
			// Don't throw, just return empty so we fall back to JWT data
			return {};
		}
	}

	/**
	 * Receive the firebase token to use it to authenticate against the taxpeer backend
	 * Sets the token in the cookie.
	 *
	 * @param data - The login request data containing email and password
	 * @param refreshCaptcha - Optional function to refresh the captcha token before making the backend call
	 * @returns The login response from the backend
	 * @throws Error if the email or password is missing or if the login fails
	 */
	public async login(
		data: LoginRequest,
		refreshCaptcha?: () => Promise<string>,
	): Promise<LoginResponse> {
		try {
			if (!data.email || !data.password) {
				return Promise.reject("Email and password are required");
			}

			// First, authenticate with Firebase
			const userCredential = await signInWithEmailAndPassword(
				this.auth,
				data.email,
				data.password,
			);

			// Get the Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			// Use the token to authenticate with our backend
			// Pass refreshCaptcha to ensure a fresh captcha token
			const response = await this.upgradeFirebaseIdToJWT(
				firebaseToken,
				refreshCaptcha,
			);

			console.log("[Auth] Login successful!");
			return Promise.resolve(response);
		} catch (error: unknown) {
			captureAuthException(error, "login_email_password");
			if (error instanceof Error) {
				toast.error(i18n.t("authService.checkCredentials"));
				return Promise.reject(error.message);
			}
			return Promise.reject(i18n.t("authService.loginError"));
		}
	}

	/**
	 * Send a verification email to the user
	 */
	public async sendEmailVerification(): Promise<void> {
		const currentUser = this.auth.currentUser;
		if (!currentUser) {
			throw new Error("No user is currently signed in");
		}

		await sendEmailVerification(currentUser);
		toast.success(i18n.t("authService.verificationEmailSent"));
	}

	/**
	 * Register with email and password using Firebase
	 *
	 */
	public async register(data: RegisterRequest): Promise<RegisterResponse> {
		try {
			if (!data.email || !data.password) {
				throw new Error("Email and password are required");
			}

			const userCredential = await createUserWithEmailAndPassword(
				this.auth,
				data.email,
				data.password,
			);

			// Send verification email
			// Get current language from i18next or browser
			const currentLang =
				localStorage.getItem("i18nextLng") || navigator.language;
			// Normalize to 'de' or 'en' (Firebase supports standard language codes)
			const languageCode = currentLang.startsWith("de") ? "de" : "en";

			// For local development: Add actionCodeSettings to redirect to localhost
			const actionCodeSettings =
				window.location.hostname === "localhost"
					? {
							url: window.location.origin,
							handleCodeInApp: false,
						}
					: undefined;

			// Set the language for this email
			this.auth.languageCode = languageCode;

			await sendEmailVerification(userCredential.user, actionCodeSettings);

			if (!userCredential.user.email || !userCredential.user.uid) {
				return Promise.reject("Email or uid is required");
			}

			toast.success(i18n.t("authService.registrationSuccess"));

			return {
				id: userCredential.user.uid,
				email: userCredential.user.email,
			};
		} catch (error: unknown) {
			captureAuthException(error, "register_email_password");
			// Check if this is a Firebase error for email already in use
			// Firebase errors can have the code in different formats:
			// - error.code === "auth/email-already-in-use"
			// - error.message.includes("EMAIL_EXISTS")
			// - error.message.includes("auth/email-already-in-use")
			const isEmailExistsError =
				(error as any)?.code === "auth/email-already-in-use" ||
				(error instanceof Error &&
					(error.message.includes("EMAIL_EXISTS") ||
						error.message.includes("email-already-in-use") ||
						error.message.includes("email already exists")));

			if (isEmailExistsError) {
				toast.error(i18n.t("authService.emailAlreadyRegistered"));
				return Promise.reject(i18n.t("authService.emailAlreadyRegisteredFull"));
			}

			// Handle other errors
			if (error instanceof Error) {
				toast.error(i18n.t("authService.registrationError"));
				return Promise.reject(error.message);
			}

			return Promise.reject(i18n.t("authService.registrationErrorFull"));
		}
	}

	/**
	 * Forgot password
	 *
	 * Sends a password reset email to the user
	 * @param email - The email of the user to send the password reset email to
	 * @throws Error if the email is missing or if the password reset email fails to send
	 */
	public async forgotPassword(email: string): Promise<void> {
		const auth = getAuth();

		// Get current language from i18next or browser
		const currentLang =
			localStorage.getItem("i18nextLng") || navigator.language;
		// Normalize to 'de' or 'en' (Firebase supports standard language codes)
		const languageCode = currentLang.startsWith("de") ? "de" : "en";

		// For local development: Add continueUrl to redirect to localhost
		// Firebase action URL should be set to staging, but this will redirect back to localhost
		const actionCodeSettings =
			window.location.hostname === "localhost"
				? {
						url: window.location.origin,
						handleCodeInApp: false,
					}
				: undefined;

		// Set the language for this email
		auth.languageCode = languageCode;

		await sendPasswordResetEmail(auth, email, actionCodeSettings);
	}

	/**
	 * Authenticate with the backend using Firebase token and receive a JWT token in return
	 *
	 * This will present the firebase token to the backend, receive a token in return on success
	 * @param firebaseToken - The firebase token to authenticate with the backend
	 * @param refreshCaptcha - Optional function to refresh the captcha token before making the backend call
	 * @returns The auth response from the backend
	 * @throws Error if the authentication fails
	 */
	public async upgradeFirebaseIdToJWT(
		firebaseToken: string,
		refreshCaptcha?: () => Promise<string>,
	): Promise<AuthResponse> {
		try {
			// If a captcha refresh function is provided, refresh the token before the backend call
			if (refreshCaptcha) {
				try {
					await refreshCaptcha();
				} catch (tokenError) {
					captureAuthException(tokenError, "upgrade_firebase_id_captcha");
					throw new Error(
						tokenError instanceof Error
							? tokenError.message
							: "Failed to verify you're human. Please try again.",
					);
				}
			}
			const response = await this.baseApi.post<
				AuthResponse,
				{ firebaseToken: string }
			>("auth/login", { firebaseToken });
			console.log("[Auth] Backend login successful");
			return response;
		} catch (error: unknown) {
			console.error("[Auth] upgradeFirebaseIdToJWT failed:", error);
			captureAuthException(error, "upgrade_firebase_id_to_jwt");
			// Normal error handling for other cases
			if (error instanceof Error) {
				return Promise.reject(error.message);
			}
			return Promise.reject(i18n.t("authService.authenticationError"));
		}
	}

	/**
	 * Refresh the backend JWT with a fresh Firebase ID token.
	 *
	 * Used for background/session refreshes and intentionally does not use the
	 * Turnstile-protected login endpoint.
	 */
	public async refreshUserToken(firebaseToken: string): Promise<AuthResponse> {
		try {
			const response = await this.baseApi.post<
				AuthResponse,
				{ firebaseToken: string }
			>("auth/refresh-user-token", { firebaseToken });
			console.log("[Auth] User token refresh successful");
			return response;
		} catch (error: unknown) {
			console.error("[Auth] refreshUserToken failed:", error);
			captureAuthException(error, "refresh_user_token");
			if (error instanceof Error) {
				return Promise.reject(error.message);
			}
			return Promise.reject(i18n.t("authService.authenticationError"));
		}
	}

	/**
	 * Logout the current user
	 */
	public async logout(): Promise<void> {
		try {
			await signOut(this.auth);
		} catch (error: unknown) {
			if (error instanceof Error) {
				captureAuthException(error, "logout");
				throw new Error(error.message);
			}
		}
	}

	/**
	 * Login with Google
	 *
	 * @param refreshCaptcha - Optional function to refresh the captcha token before making the backend call
	 * @returns The login response from the backend
	 * @throws Error if the login with Google fails
	 */
	public async loginWithGoogle(
		refreshCaptcha?: () => Promise<string>,
	): Promise<LoginResponse | null> {
		try {
			const provider = new GoogleAuthProvider();
			const userCredential = await this.signInWithProvider(provider, "google");
			if (!userCredential) {
				return null; // redirect flow – page is navigating away
			}

			// Get the Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			// Use the token to authenticate with our backend
			const response = await this.upgradeFirebaseIdToJWT(
				firebaseToken,
				refreshCaptcha,
			);

			return Promise.resolve(response);
		} catch (error: unknown) {
			if (isInAppBrowserAuthError(error)) {
				return null;
			}
			captureAuthException(error, "login_google");
			if (error instanceof Error) {
				toast.error(i18n.t("authService.googleLoginError"));
				return Promise.reject(error.message);
			}
			return Promise.reject(i18n.t("authService.googleLoginError"));
		}
	}

	/**
	 * Login with Apple
	 *
	 * @param refreshCaptcha - Optional function to refresh the captcha token before making the backend call
	 * @returns The login response from the backend
	 * @throws Error if the login with Apple fails
	 */
	public async loginWithApple(
		refreshCaptcha?: () => Promise<string>,
	): Promise<LoginResponse | null> {
		try {
			const provider = new OAuthProvider("apple.com");
			provider.addScope("email");
			provider.addScope("name");

			const userCredential = await this.signInWithProvider(provider, "apple");
			if (!userCredential) {
				return null; // redirect flow – page is navigating away
			}

			const firebaseToken = await userCredential.user.getIdToken();

			// Use the token to authenticate with our backend
			const response = await this.upgradeFirebaseIdToJWT(
				firebaseToken,
				refreshCaptcha,
			);
			return Promise.resolve(response);
		} catch (error: unknown) {
			if (isInAppBrowserAuthError(error)) {
				return null;
			}
			captureAuthException(error, "login_apple");
			if (error instanceof Error) {
				toast.error(i18n.t("authService.appleLoginError"));
				return Promise.reject(error.message);
			}
			return Promise.reject(i18n.t("authService.appleLoginError"));
		}
	}

	/**
	 * Handle the redirect result after a signInWithRedirect flow.
	 * Should be called once on app mount. Returns null quickly when no
	 * redirect is pending.
	 */
	public async handleRedirectResult(): Promise<LoginResponse | null> {
		try {
			const result = await getRedirectResult(this.auth);
			if (result) {
				const firebaseToken = await result.user.getIdToken();
				const response = await this.upgradeFirebaseIdToJWT(firebaseToken);
				return response;
			}
		} catch (error) {
			console.error("[Auth] Error handling redirect result:", error);
			captureAuthException(error, "handle_redirect_result");
			toast.error(i18n.t("authService.authenticationError"));
		}
		return null;
	}

	/**
	 * This function updates the user's display name via firebase
	 * @param newDisplayName
	 */
	public async updateUserDisplayName(newDisplayName: string): Promise<boolean> {
		try {
			const currentUser = this.auth.currentUser;

			if (!currentUser) {
				throw new Error("No user is currently signed in");
			}

			await updateProfile(currentUser, { displayName: newDisplayName });
			return Promise.resolve(true);
		} catch (error: unknown) {
			captureAuthException(error, "update_display_name");
			if (error instanceof Error) {
				toast.error(i18n.t("authService.updateDisplayNameError"));
				return Promise.resolve(false);
			}
			return Promise.reject(i18n.t("authService.updateDisplayNameError"));
		}
	}

	/**
	 * Updates the current user's email address
	 *
	 * @param newEmail - The new email address
	 * @returns A promise that resolves when the verification email has been sent
	 * @throws Error if the email update process fails
	 */
	public async updateUserEmail(newEmail: string): Promise<boolean> {
		try {
			const currentUser = this.auth.currentUser;

			if (!currentUser) {
				throw new Error("No user is currently signed in");
			}

			// Configure the action code settings with redirect URL
			const actionCodeSettings = {
				url: `${window.location.origin}/`,
				handleCodeInApp: true,
			};

			// Send verification email to the new address before updating with redirect URL
			await verifyBeforeUpdateEmail(currentUser, newEmail, actionCodeSettings);

			toast.success(
				i18n.t("authService.emailVerificationSent", { email: newEmail }),
			);

			return Promise.resolve(true);
		} catch (error: unknown) {
			captureAuthException(error, "update_email");
			if (error instanceof Error) {
				// Handle specific error cases
				if (error.message.includes("auth/requires-recent-login")) {
					toast.error(i18n.t("authService.recentLoginRequired"));
				} else {
					toast.error(i18n.t("authService.updateEmailError"));
					return Promise.resolve(false);
				}
			}
			return Promise.reject(i18n.t("authService.updateEmailError"));
		}
	}

	/**
	 * Login with a custom authentication token
	 *
	 * @param customToken - The custom authentication token
	 * @returns The login response from the backend
	 * @throws Error if the login with custom token fails
	 */
	public async loginWithCustomToken(
		customToken: string,
	): Promise<LoginResponse> {
		try {
			// Sign in with the custom token
			const userCredential = await signInWithCustomToken(
				this.auth,
				customToken,
			);

			console.log("Signing in with custom token", userCredential);

			// Get the Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			console.log("Firebase token from custom token", firebaseToken);

			// Use the token to authenticate with our backend
			const response = await this.upgradeFirebaseIdToJWT(firebaseToken);

			return Promise.resolve(response);
		} catch (error: unknown) {
			captureAuthException(error, "login_custom_token");
			if (error instanceof Error) {
				toast.error(i18n.t("authService.tokenLoginError"));
				return Promise.reject(error.message);
			}
			return Promise.reject(i18n.t("authService.tokenLoginError"));
		}
	}

	/**
	 * Sign into Firebase with a custom token WITHOUT logging into the backend.
	 *
	 * This is used to carry the same Firebase UID across domains (e.g. landing page -> app)
	 * while keeping the funnel flow lightweight. The backend `/auth/login` route requires an email,
	 * so we must not call it for pre-upgrade (email-less) users.
	 *
	 * @param customToken - The custom authentication token
	 * @returns The Firebase ID token for the signed-in user
	 */
	public async signInWithCustomTokenFirebaseOnly(
		customToken: string,
	): Promise<string> {
		const userCredential = await signInWithCustomToken(this.auth, customToken);
		return await userCredential.user.getIdToken();
	}

	/**
	 * Login anonymously
	 * @returns The login response containing the token
	 */
	public async loginAnonymously(): Promise<AuthResponse> {
		try {
			const userCredential = await signInAnonymously(this.auth);
			const firebaseToken = await userCredential.user.getIdToken();

			// For anonymous users, we might not always want to upgrade to JWT immediately
			// but keeping the signature consistent with other login methods is good practice.
			// However, based on the specific requirement in FinishSignupPage,
			// we are primarily interested in the firebase authentication state.
			// But since the method signature implies returning a token/response...

			// If the backend has an endpoint to exchange anonymous firebase token for a JWT,
			// we would call it here. If not, we can just return a dummy or the firebase token
			// if the caller expects { token: string }.

			// Re-using upgradeFirebaseIdToJWT might fail if the backend strictly requires
			// verified emails for the main /auth/login endpoint, but usually it handles it.
			// Let's check upgradeFirebaseIdToJWT implementation or just return the firebase token
			// wrapped in AuthResponse if we don't want to force backend session creation yet.

			// Actually, the caller in FinishSignupPage explicitly DOES NOT want to set the global
			// auth token yet. So returning just the firebase token (or even void) is what matters
			// for the side effect (firebase auth state).

			return { token: firebaseToken };
		} catch (error) {
			console.error("Anonymous login failed", error);
			captureAuthException(error, "login_anonymously");
			throw error;
		}
	}

	/**
	 * Link the current anonymous user with an email and password credential.
	 * @param request The register request containing email and password
	 * @returns The login response
	 */
	public async upgradeAnonymousAccount(
		request: RegisterRequest,
	): Promise<LoginResponse> {
		try {
			if (!this.auth.currentUser) {
				throw new Error("No user logged in to upgrade");
			}

			// Create email credential
			const credential = EmailAuthProvider.credential(
				request.email,
				request.password,
			);

			// Link the anonymous user with the email credential
			const userCredential = await linkWithCredential(
				this.auth.currentUser,
				credential,
			);

			// Send verification email
			await sendEmailVerification(userCredential.user);

			// Get the new token
			const firebaseToken = await userCredential.user.getIdToken(true);

			// Upgrade to JWT
			const response = await this.upgradeFirebaseIdToJWT(firebaseToken);
			return response;
		} catch (error: unknown) {
			console.error("Failed to upgrade anonymous account", error);
			captureAuthException(error, "upgrade_anonymous_account_email");
			if (error instanceof Error) {
				const errorCode = (error as any).code;

				// If the provider is already linked to THIS user, just proceed to token exchange
				if (errorCode === "auth/provider-already-linked") {
					console.log(
						"Provider already linked, attempting to sync with backend...",
					);
					if (this.auth.currentUser) {
						const firebaseToken = await this.auth.currentUser.getIdToken(true);
						return await this.upgradeFirebaseIdToJWT(firebaseToken);
					}
				}

				if (errorCode === "auth/credential-already-in-use") {
					return Promise.reject(
						"This email is already associated with another account.",
					);
				}
				if (errorCode === "auth/email-already-in-use") {
					return Promise.reject("This email is already in use.");
				}
				if (errorCode === "auth/weak-password") {
					return Promise.reject("Password is too weak.");
				}
				return Promise.reject(error.message);
			}
			return Promise.reject("Failed to upgrade account");
		}
	}

	/**
	 * Sync the upgraded account to the backend so the backend has the correct email and isAnonymous flag
	 * @param upgradedEmail
	 * @returns
	 */
	public async syncUpgradedAccountToBackend(
		upgradedEmail?: string,
	): Promise<void> {
		const currentUser = this.auth.currentUser;
		if (!currentUser) {
			throw new Error("No authenticated Firebase user after upgrade");
		}

		// Force refresh so email is present in the token after linking
		const firebaseToken = await currentUser.getIdToken(true);

		const email = (upgradedEmail ?? currentUser.email)?.toLowerCase();
		if (!email) {
			throw new Error("Upgraded email is missing");
		}

		await this.postUpgradeAnonymousAccountToBackend(firebaseToken, email);
	}

	/**
	 * Link the current anonymous user with a Google account.
	 * @returns The login response
	 */
	public async upgradeAnonymousAccountWithGoogle(): Promise<LoginResponse> {
		try {
			blockOAuthInInAppBrowser();
			if (!this.auth.currentUser) {
				throw new Error("No user logged in to upgrade");
			}

			const provider = new GoogleAuthProvider();
			// Use linkWithPopup to preserve the current user UID
			const userCredential = await linkWithPopup(
				this.auth.currentUser,
				provider,
			);

			// Get the new token
			const firebaseToken = await userCredential.user.getIdToken(true);

			// Upgrade to JWT
			const response = await this.upgradeFirebaseIdToJWT(firebaseToken);
			return response;
		} catch (error: unknown) {
			if (isInAppBrowserAuthError(error)) {
				throw error;
			}
			console.error("Failed to upgrade anonymous account with Google", error);
			captureAuthException(error, "upgrade_anonymous_account_google");
			if (error instanceof Error) {
				const errorCode = (error as any).code;

				// If already linked, proceed to sync
				if (errorCode === "auth/provider-already-linked") {
					console.log(
						"Google already linked, attempting to sync with backend...",
					);
					if (this.auth.currentUser) {
						const firebaseToken = await this.auth.currentUser.getIdToken(true);
						return await this.upgradeFirebaseIdToJWT(firebaseToken);
					}
				}

				if (errorCode === "auth/credential-already-in-use") {
					return Promise.reject(
						"This Google account is already linked to another user.",
					);
				}
				return Promise.reject(error.message);
			}
			return Promise.reject("Failed to link Google account");
		}
	}
}

export default AuthService;
